import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import SolarMonitorDashboard from './components/SolarMonitorDashboard';
import PanelVoltagePage from './components/PanelVoltagePage';
import ReportPage from './components/ReportPage';
import ReportGraphsPage from './components/ReportGraphsPage';
import ReportWorkingHoursPage from './components/ReportWorkingHoursPage';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import OrganizationCreatePage from './components/OrganizationCreatePage';
import SuperAdminUsersOrgsPage from './components/SuperAdminUsersOrgsPage';
import UserSettingsPage from './components/UserSettingsPage';
import { fetchSolarData } from './services/apiService';
import { getUser, isSuperAdminUser } from './services/authService';

function ProtectedRoute({ children }) {
  return getUser() ? children : <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  const u = getUser();
  if (u) {
    return <Navigate to={isSuperAdminUser(u) ? '/admin/organizations/create' : '/'} replace />;
  }
  return children;
}

function AdminRoute({ children }) {
  const u = getUser();
  return isSuperAdminUser(u) ? children : <Navigate to="/" replace />;
}

function UserDashboardRoute({ children }) {
  const u = getUser();
  if (isSuperAdminUser(u)) {
    return <Navigate to="/admin/organizations/create" replace />;
  }
  return children;
}

function DashboardWrapper() {
  const user = getUser();
  if (isSuperAdminUser(user)) {
    return <Navigate to="/admin/organizations/create" replace />;
  }
  return <SolarDashboardContent />;
}

function SolarDashboardContent() {
  const [solarData, setSolarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchSolarData();
        // Log to verify current data is received
        if (data && data.current) {
          console.log('Current data received:', {
            c1: data.current.c1,
            c2: data.current.c2,
            c3: data.current.c3
          });
        }
        setSolarData(data);
        setError(null);
      } catch (err) {
        setError('Failed to load solar data. Please check your connection.');
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    // Load data immediately
    loadData();
    
    // Refresh data every 5 seconds to get the most recent data
    const interval = setInterval(() => {
      loadData();
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading Solar Monitor Data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
      </div>
    );
  }

  return (
    <div className="App">
      <SolarMonitorDashboard data={solarData} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
        <Route path="/" element={<ProtectedRoute><DashboardWrapper /></ProtectedRoute>} />
        <Route path="/panel-voltage" element={<ProtectedRoute><UserDashboardRoute><PanelVoltagePage /></UserDashboardRoute></ProtectedRoute>} />
        <Route path="/report" element={<ProtectedRoute><UserDashboardRoute><ReportPage /></UserDashboardRoute></ProtectedRoute>} />
        <Route path="/report/graphs" element={<ProtectedRoute><UserDashboardRoute><ReportGraphsPage /></UserDashboardRoute></ProtectedRoute>} />
        <Route path="/report/working-hours" element={<ProtectedRoute><UserDashboardRoute><ReportWorkingHoursPage /></UserDashboardRoute></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><UserSettingsPage /></ProtectedRoute>} />
        <Route path="/admin/organizations/create" element={<AdminRoute><OrganizationCreatePage /></AdminRoute>} />
        <Route path="/admin/users-and-organisations" element={<AdminRoute><SuperAdminUsersOrgsPage /></AdminRoute>} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;

